/**
 * Settings → Documents (C0): live preview of the header and footer, test PDF (P1-01),
 * layout versions (publish, history, restore) and numbering formats.
 */

import {
  type DocumentLayout,
  type NumberingSequence,
  type ResetEnum,
  documentsFilesLinkRetrieve,
  getDocumentsLayoutsCurrentRetrieveQueryKey,
  getDocumentsLayoutsListQueryKey,
  getDocumentsLayoutsPreviewRetrieveQueryKey,
  getDocumentsNumberingListQueryKey,
  useDocumentsFilesRetrieve,
  useDocumentsFilesSpecimenCreate,
  useDocumentsLayoutsCreate,
  useDocumentsLayoutsCurrentRetrieve,
  useDocumentsLayoutsList,
  useDocumentsLayoutsPreviewRetrieve,
  useDocumentsLayoutsRestoreCreate,
  useDocumentsNumberingList,
  useDocumentsNumberingPartialUpdate,
} from "@agencyos/api-client";
import {
  Badge,
  Button,
  GlassCard,
  SegmentedControl,
  SelectField,
  Skeleton,
  SoftToggle,
  TextAreaField,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useCanEditSettings } from "../lib/settings";

const DOC_LANGUAGES = ["fr", "en"] as const;
type DocLanguage = (typeof DOC_LANGUAGES)[number];
type ByLanguage<T> = Partial<Record<string, T>>;

function useLanguageOptions() {
  const { t } = useTranslation();
  return DOC_LANGUAGES.map((l) => ({ value: l, label: t(`docs.languages.${l}`) }));
}

/** Waits for the background printing, then asks for a signed, time-limited link. */
export function SpecimenResult({ id }: { id: string }) {
  const { t } = useTranslation();
  const document = useDocumentsFilesRetrieve(id, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.status === 200 && data.data.status === "pending" ? 1500 : false;
      },
    },
  });
  const [link, setLink] = useState<{ url: string; minutes: number } | null>(null);
  const status = document.data?.status === 200 ? document.data.data.status : "pending";

  useEffect(() => {
    if (status !== "ready" || link) return;
    void documentsFilesLinkRetrieve(id).then((response) => {
      if (response.status === 200)
        setLink({ url: response.data.url, minutes: Math.round(response.data.expires_in / 60) });
    });
  }, [status, id, link]);

  if (status === "failed")
    return (
      <p role="alert" className="ko">
        {t("docs.testPdfFailed")}
      </p>
    );
  if (!link) return <p role="status">{t("docs.testPdfWorking")}</p>;
  return (
    <div role="status" className="stack">
      <p className="ok">{t("docs.testPdfReady")}</p>
      <div className="row">
        <a className="ao-button ao-button--primary" href={link.url} target="_blank" rel="noreferrer">
          {t("docs.testPdfOpen")}
        </a>
        <span className="muted">{t("docs.linkExpires", { minutes: link.minutes })}</span>
      </div>
    </div>
  );
}

function PreviewCard() {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<DocLanguage>("fr");
  const canEdit = useCanEditSettings();
  // Always fresh: a phone number changed in Settings → Agency must show here at once (P1-01).
  const preview = useDocumentsLayoutsPreviewRetrieve(
    { language },
    { query: { staleTime: 0, refetchOnMount: "always" } },
  );
  const specimen = useDocumentsFilesSpecimenCreate();
  const languageOptions = useLanguageOptions();
  const html = preview.data?.status === 200 ? preview.data.data.html : "";
  return (
    <GlassCard as="section" className="stack preview-card">
      <div className="row between">
        <h2>{t("docs.preview")}</h2>
        <SegmentedControl
          label={t("docs.previewLanguage")}
          value={language}
          options={languageOptions}
          onChange={(value) => setLanguage(value)}
        />
      </div>
      {preview.isError ? (
        <p role="alert" className="ko">
          {t("common.error")}
        </p>
      ) : html ? (
        <iframe
          className="doc-preview"
          title={t("docs.preview")}
          sandbox=""
          srcDoc={html}
          data-testid="layout-preview"
        />
      ) : (
        <Skeleton style={{ height: 320 }} />
      )}
      {canEdit ? (
        <div className="stack">
          <div className="row">
            <Button
              disabled={specimen.isPending}
              onClick={() => specimen.mutate({ data: { language } })}
            >
              {t("docs.testPdf")}
            </Button>
          </div>
          {specimen.data?.status === 201 ? (
            <SpecimenResult key={specimen.data.data.id} id={specimen.data.data.id} />
          ) : null}
        </div>
      ) : null}
    </GlassCard>
  );
}

function linesToText(value: unknown, language: DocLanguage): string {
  const lines = (value as ByLanguage<string[]> | undefined)?.[language];
  return Array.isArray(lines) ? lines.join("\n") : "";
}

function textToLines(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function LayoutEditor({ layout }: { layout: DocumentLayout }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useCanEditSettings();
  const [language, setLanguage] = useState<DocLanguage>("fr");
  const languageOptions = useLanguageOptions();
  const initial = () =>
    Object.fromEntries(
      DOC_LANGUAGES.map((l) => [
        l,
        {
          header: linesToText(layout.header_lines, l),
          footer: linesToText(layout.footer_lines, l),
          notice: String((layout.confidentiality_notice as ByLanguage<string> | undefined)?.[l] ?? ""),
        },
      ]),
    ) as Record<DocLanguage, { header: string; footer: string; notice: string }>;
  const [texts, setTexts] = useState(initial);
  const [showLogo, setShowLogo] = useState(layout.show_logo ?? true);
  const [showPages, setShowPages] = useState(layout.show_page_numbers ?? true);
  const [note, setNote] = useState("");
  const publish = useDocumentsLayoutsCreate({
    mutation: {
      onSuccess: () => {
        setNote("");
        for (const key of [
          getDocumentsLayoutsCurrentRetrieveQueryKey(),
          getDocumentsLayoutsListQueryKey(),
          getDocumentsLayoutsPreviewRetrieveQueryKey(),
        ])
          void queryClient.invalidateQueries({ queryKey: key.slice(0, 1) });
      },
    },
  });
  const errors = fieldErrors(publish.error);
  const current = texts[language];
  const set = (part: "header" | "footer" | "notice", value: string) =>
    setTexts((all) => ({ ...all, [language]: { ...all[language], [part]: value } }));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const pick = <T,>(fn: (l: DocLanguage) => T) =>
      Object.fromEntries(DOC_LANGUAGES.map((l) => [l, fn(l)]));
    publish.mutate({
      data: {
        header_lines: pick((l) => textToLines(texts[l].header)),
        footer_lines: pick((l) => textToLines(texts[l].footer)),
        confidentiality_notice: pick((l) => texts[l].notice.trim()),
        show_logo: showLogo,
        show_page_numbers: showPages,
        change_note: note,
      },
    });
  };
  const languageLabel = t(`docs.languages.${language}`);

  return (
    <GlassCard as="section" className="stack">
      <div className="row between">
        <h2>{t("docs.layout")}</h2>
        <Badge tone="success">
          {t("docs.version", { version: layout.version })} · {t("docs.current")}
        </Badge>
      </div>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <SegmentedControl
          label={t("docs.previewLanguage")}
          value={language}
          options={languageOptions}
          onChange={(value) => setLanguage(value)}
        />
        <p className="ao-field__hint">{t("docs.linesHint")}</p>
        <TextAreaField
          label={t("docs.headerLines", { language: languageLabel })}
          rows={7}
          value={current.header}
          disabled={!canEdit}
          error={errors.header_lines}
          onChange={(e) => set("header", e.target.value)}
        />
        <TextAreaField
          label={t("docs.footerLines", { language: languageLabel })}
          rows={3}
          value={current.footer}
          disabled={!canEdit}
          error={errors.footer_lines}
          onChange={(e) => set("footer", e.target.value)}
        />
        <TextField
          label={t("docs.notice", { language: languageLabel })}
          value={current.notice}
          disabled={!canEdit}
          error={errors.confidentiality_notice}
          onChange={(e) => set("notice", e.target.value)}
        />
        <SoftToggle label={t("docs.showLogo")} checked={showLogo} disabled={!canEdit} onChange={setShowLogo} />
        <SoftToggle label={t("docs.showPages")} checked={showPages} disabled={!canEdit} onChange={setShowPages} />
        {canEdit ? (
          <>
            <TextField label={t("docs.changeNote")} value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} />
            <div className="row">
              <Button type="submit" disabled={publish.isPending}>
                {publish.isPending ? t("common.loading") : t("docs.publish")}
              </Button>
              <p role="status" className={publish.isError ? "ko" : "ok"}>
                {publish.data?.status === 201
                  ? t("docs.published", { version: publish.data.data.version })
                  : publish.isError
                    ? t("common.fixErrors")
                    : ""}
              </p>
            </div>
          </>
        ) : null}
      </form>
    </GlassCard>
  );
}

function VersionHistory() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useCanEditSettings();
  const versions = useDocumentsLayoutsList();
  const restore = useDocumentsLayoutsRestoreCreate({
    mutation: { onSuccess: () => void queryClient.invalidateQueries() },
  });
  if (versions.data?.status !== 200) return null;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("docs.history")}</h2>
      <ul className="plain-list">
        {versions.data.data.results.map((version) => (
          <li key={version.id} className="row between">
            <span>
              <strong>{t("docs.version", { version: version.version })}</strong>{" "}
              <span className="muted">
                {/* Day/month/year in English too (Cameroon), in Douala time. */}
                {new Date(version.created_at).toLocaleDateString(i18n.language === "en" ? "en-GB" : i18n.language, {
                  timeZone: "Africa/Douala",
                })}
                {version.change_note ? ` · ${version.change_note}` : ""}
              </span>
            </span>
            {version.is_current ? (
              <Badge tone="success">{t("docs.current")}</Badge>
            ) : canEdit ? (
              <Button
                variant="ghost"
                disabled={restore.isPending}
                onClick={() => restore.mutate({ id: version.id })}
              >
                {t("docs.restore")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

function NumberingRow({ sequence }: { sequence: NumberingSequence }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useCanEditSettings();
  const [pattern, setPattern] = useState(sequence.pattern);
  const [reset, setReset] = useState<ResetEnum>(sequence.reset ?? "yearly");
  const update = useDocumentsNumberingPartialUpdate({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getDocumentsNumberingListQueryKey() }),
    },
  });
  const errors = fieldErrors(update.error);
  return (
    <form
      className="stack numbering-row"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ id: sequence.id, data: { pattern, reset } });
      }}
    >
      <h3>{sequence.label}</h3>
      <TextField
        label={t("docs.pattern")}
        value={pattern}
        hint={t("docs.patternHint")}
        error={errors.pattern}
        disabled={!canEdit}
        onChange={(e) => setPattern(e.target.value)}
      />
      <SelectField
        label={t("docs.reset")}
        value={reset}
        disabled={!canEdit}
        options={[
          { value: "yearly", label: t("docs.resetYearly") },
          { value: "never", label: t("docs.resetNever") },
        ]}
        onChange={(e) => setReset(e.target.value as ResetEnum)}
      />
      <p className="muted">{t("docs.next", { number: sequence.next_number })}</p>
      {canEdit ? (
        <div className="row">
          <Button type="submit" variant="secondary" disabled={update.isPending}>
            {t("common.save")}
          </Button>
          {update.isSuccess ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

function NumberingCard() {
  const { t } = useTranslation();
  const sequences = useDocumentsNumberingList();
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("docs.numbering")}</h2>
      <p className="muted">{t("docs.numberingIntro")}</p>
      {sequences.data?.status === 200 ? (
        <div className="grid">
          {sequences.data.data.results.map((sequence) => (
            <NumberingRow key={sequence.id} sequence={sequence} />
          ))}
        </div>
      ) : (
        <Skeleton />
      )}
    </GlassCard>
  );
}

export function DocumentsPage() {
  const { t } = useTranslation();
  const layout = useDocumentsLayoutsCurrentRetrieve();
  return (
    <SettingsLayout title={t("docs.title")} intro={t("docs.intro")}>
      <PreviewCard />
      {layout.data?.status === 200 ? (
        <LayoutEditor key={layout.data.data.id} layout={layout.data.data} />
      ) : (
        <GlassCard aria-busy="true">
          <Skeleton />
        </GlassCard>
      )}
      <VersionHistory />
      <NumberingCard />
    </SettingsLayout>
  );
}
